/**
 * Settings (GDD §8, §9): auto-fire, left-handed layout, control sensitivity, haptics, music and effects volume, and
 * mute. Every change applies at once and is saved on the device.
 */
import { config } from '../core/config.ts';
import type { Settings } from '../storage/storage.ts';
import { add, el } from './dom.ts';

export interface SettingsPanel {
  el: HTMLElement;
  /** Focus order for keyboard and gamepad. */
  focusables: HTMLElement[];
  /** Shows the given values. */
  sync(s: Settings): void;
}

type Toggle = 'autoFire' | 'leftHanded' | 'haptics' | 'mute';
type Slider = 'sensitivity' | 'music' | 'effects';

export function buildSettings(
  doc: Document,
  initial: Settings,
  change: (s: Settings) => void,
  back: () => void,
  hapticsSupported: boolean,
): SettingsPanel {
  let cur: Settings = { ...initial };
  const box = el(doc, 'div', 'hw-menu hw-glass');
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-label', 'Settings');
  add(box, el(doc, 'h2', 'hw-menu-title', 'SETTINGS'));
  const focusables: HTMLElement[] = [];
  const toggles = new Map<Toggle, HTMLButtonElement>();
  const sliders = new Map<Slider, { input: HTMLInputElement; out: HTMLOutputElement }>();

  const commit = (next: Settings): void => {
    cur = next;
    change({ ...cur });
  };

  function toggle(key: Toggle, label: string): void {
    const b = add(box, el(doc, 'button', 'hw-row'));
    b.type = 'button';
    b.setAttribute('role', 'switch');
    b.dataset.setting = key;
    add(b, el(doc, 'span', '', label), el(doc, 'span', 'hw-switch'));
    b.addEventListener('click', () => {
      commit({ ...cur, [key]: !cur[key] });
      b.setAttribute('aria-checked', String(cur[key]));
    });
    toggles.set(key, b);
    focusables.push(b);
  }

  const fmt = (key: Slider, v: number): string => (key === 'sensitivity' ? `${v.toFixed(2)}×` : `${Math.round(v)}`);

  function slider(key: Slider, label: string, min: number, max: number, step: number): void {
    const row = add(box, el(doc, 'label', 'hw-row'));
    add(row, el(doc, 'span', '', label));
    const wrap = add(row, el(doc, 'span', 'hw-slider'));
    const input = add(wrap, el(doc, 'input'));
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.dataset.setting = key;
    input.setAttribute('aria-label', label);
    const out = add(wrap, el(doc, 'output'));
    input.addEventListener('input', () => {
      const raw = Number(input.value);
      const v = key === 'sensitivity' ? raw : raw / 100;
      out.textContent = fmt(key, raw);
      commit({ ...cur, [key]: v });
    });
    sliders.set(key, { input, out });
    focusables.push(input);
  }

  const sens = config.flight.sensitivity;
  toggle('autoFire', 'AUTO-FIRE · TOUCH');
  toggle('leftHanded', 'LEFT-HANDED');
  slider('sensitivity', 'SENSITIVITY', sens.min, sens.max, 0.05);
  toggle('haptics', 'HAPTICS');
  slider('music', 'MUSIC', 0, 100, 5);
  slider('effects', 'EFFECTS', 0, 100, 5);
  toggle('mute', 'MUTE');
  if (!hapticsSupported) add(box, el(doc, 'p', 'hw-note', 'This device can’t vibrate, so haptics stay silent here.'));
  const done = add(box, el(doc, 'button', 'hw-menu-btn hw-primary', 'BACK'));
  done.type = 'button';
  done.addEventListener('click', back);
  focusables.push(done);

  const panel: SettingsPanel = {
    el: box,
    focusables,
    sync(s) {
      cur = { ...s };
      for (const [k, b] of toggles) b.setAttribute('aria-checked', String(s[k]));
      for (const [k, { input, out }] of sliders) {
        const raw = k === 'sensitivity' ? s[k] : Math.round(s[k] * 100);
        input.value = String(raw);
        out.textContent = fmt(k, raw);
      }
    },
  };
  panel.sync(initial);
  return panel;
}
